/**
 * Estimates the server clock from ping/pong round trips.
 *
 * Every round trip gives a sample: offset = serverTime − (sentAt + receivedAt) / 2, i.e. the
 * server stamped its time half-way through the trip. The error of a sample is at most half
 * its round-trip time, so only the samples nearly as fast as the fastest one count, and the
 * estimate is their median (which also ignores a single outlier among them). On a mobile
 * network a stalled leg makes some round trips several times slower: those samples are
 * left out entirely instead of being outvoted, which matters most right after connecting,
 * when only the first few samples exist.
 *
 * The local side uses a monotonic clock (performance.now), never Date.now: the wall clock
 * of a phone can jump at any time (NTP, manual changes), which would break race timing.
 */

export interface ClockSyncOptions {
  /**
   * Samples are kept for this long (ms, by when their pong came back) however often the client
   * pings: every 3 s, and every second while racing. A shorter memory would let a stretch of
   * lopsided round trips (an uplink backed up for a while) replace every fast sample and shift
   * the clock mid-race, and with it the finish times...
   */
  windowMs: number;
  /** ...and at most this many of them (the latest). */
  maxSamples: number;
  /** The estimate is the median over at most this many of the fastest samples. */
  bestSamples: number;
  /**
   * serverNow() never goes backwards: small negative corrections are absorbed by briefly
   * holding the clock. A correction larger than this is applied at once instead, because
   * freezing the race clock for that long would be worse than a jump.
   */
  maxHoldMs: number;
}

export const DEFAULT_CLOCK_SYNC: ClockSyncOptions = {
  windowMs: 48_000,
  maxSamples: 64,
  bestSamples: 5,
  maxHoldMs: 1000,
};

export interface ClockSample {
  /** serverTime − localTime (ms). */
  offset: number;
  /** Round-trip time (ms). */
  rtt: number;
}

interface KeptSample extends ClockSample {
  /** Local monotonic time the pong came back. */
  at: number;
}

export class ClockSync {
  private samples: KeptSample[] = [];
  private offsetMs: number;
  private lastValue = Number.NEGATIVE_INFINITY;
  private readonly options: ClockSyncOptions;

  /**
   * @param now monotonic local clock in ms (performance.now in the browser)
   * @param wallNow wall clock used as the initial guess before any server time is known
   */
  constructor(
    private readonly now: () => number,
    wallNow: () => number = Date.now,
    options: Partial<ClockSyncOptions> = {},
  ) {
    this.options = { ...DEFAULT_CLOCK_SYNC, ...options };
    this.offsetMs = wallNow() - now();
  }

  /** Current estimate of serverTime − localTime (ms). */
  get offset(): number {
    return this.offsetMs;
  }

  /** Lowest round-trip time among the kept samples, or null before the first pong. */
  get bestRtt(): number | null {
    let best: number | null = null;
    for (const sample of this.samples) if (best === null || sample.rtt < best) best = sample.rtt;
    return best;
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  /**
   * Forgets all samples (the current estimate stays until new data arrives). Called on every
   * new connection and when the page wakes up: some monotonic clocks pause while a device
   * sleeps, so old samples may no longer describe the current offset.
   */
  reset(): void {
    this.samples = [];
  }

  /**
   * Rough estimate from a server timestamp that has just arrived (the `welcome` message).
   * Ignored once real samples exist. It underestimates by the one-way latency, so later
   * corrections move the clock forward, which never needs the monotonic hold.
   */
  seed(serverTime: number): void {
    if (this.samples.length > 0 || !Number.isFinite(serverTime)) return;
    this.offsetMs = serverTime - this.now();
  }

  /** Adds a ping/pong round trip. `sentAt`/`receivedAt` are local monotonic times. */
  addSample(sentAt: number, serverTime: number, receivedAt: number): void {
    const rtt = receivedAt - sentAt;
    if (!(rtt >= 0) || !Number.isFinite(rtt) || !Number.isFinite(serverTime)) return;
    const { samples, options } = this;
    samples.push({ rtt, offset: serverTime - (sentAt + receivedAt) / 2, at: receivedAt });
    const oldest = receivedAt - options.windowMs;
    // The new sample itself always stays.
    while (samples.length > 1 && (samples.length > options.maxSamples || samples[0]!.at < oldest)) {
      samples.shift();
    }
    this.offsetMs = estimateOffset(samples, options.bestSamples);
  }

  /** Server clock in ms since the epoch; monotonic (see ClockSyncOptions.maxHoldMs). */
  serverNow(): number {
    const value = this.now() + this.offsetMs;
    if (value >= this.lastValue || this.lastValue - value > this.options.maxHoldMs) {
      this.lastValue = value;
    }
    return this.lastValue;
  }
}

/**
 * A sample counts as "nearly as fast" as the fastest one when its round trip is at most
 * this factor plus margin of the fastest. Tuned on simulated mobile networks (see the
 * tests): it keeps ordinary jitter and drops round trips with a stalled leg.
 */
const NEAR_FASTEST_FACTOR = 1.25;
const NEAR_FASTEST_MARGIN_MS = 10;

/** Median offset over at most `best` samples whose round trip is close to the fastest one. */
export function estimateOffset(samples: readonly ClockSample[], best: number): number {
  if (samples.length === 0) throw new Error('estimateOffset: no samples');
  const byRtt = [...samples].sort((a, b) => a.rtt - b.rtt);
  const limit = byRtt[0]!.rtt * NEAR_FASTEST_FACTOR + NEAR_FASTEST_MARGIN_MS;
  const offsets = byRtt
    .filter((s) => s.rtt <= limit)
    .slice(0, Math.max(1, best))
    .map((s) => s.offset)
    .sort((a, b) => a - b);
  const mid = offsets.length >> 1;
  return offsets.length % 2 === 1 ? offsets[mid]! : (offsets[mid - 1]! + offsets[mid]!) / 2;
}
